/**
 * Impact marks on the arena (Phase 8): a ring of decal slots merged into one geometry and drawn
 * in one call. Each impact projects a small box onto the arena meshes around the hit point
 * (three's DecalGeometry) and copies the result into the oldest slot; a per-slot fade uniform
 * lets the mark age out over twenty seconds without touching the geometry again.
 */
import {
  BufferAttribute,
  BufferGeometry,
  Euler,
  Mesh,
  MeshBasicNodeMaterial,
  NormalBlending,
  type Object3D,
  Quaternion,
  Vector3,
  type Scene,
} from 'three/webgpu';
import { DecalGeometry } from 'three/addons/geometries/DecalGeometry.js';
import { attribute, type float, smoothstep, uniformArray, uv, vec3 } from 'three/tsl';

import { effectMrt } from './mrt.js';

export const DECAL_SLOTS = 24;
/** Vertices a slot can hold (a decal that needs more is trimmed; a flat floor uses far fewer). */
export const DECAL_SLOT_VERTICES = 384;
const FADE_SECONDS = 20;

const _up = new Vector3(0, 0, 1);
const _normal = new Vector3();
const _q = new Quaternion();
const _euler = new Euler();
const _position = new Vector3();
const _size = new Vector3();

interface UniformArray {
  array: number[];
  element(index: unknown): ReturnType<typeof float>;
}

export class WorldDecals {
  readonly mesh: Mesh;
  private readonly geometry: BufferGeometry;
  private readonly positions: Float32Array;
  private readonly uvs: Float32Array;
  private readonly slotOf: Float32Array;
  private readonly born: number[] = Array.from({ length: DECAL_SLOTS }, () => -1);
  private readonly fade: UniformArray;
  private readonly material: MeshBasicNodeMaterial;
  private cursor = 0;
  private readonly surfaces: Mesh[] = [];

  constructor(
    private readonly scene: Scene,
    arenaRoot: Object3D,
  ) {
    arenaRoot.traverse((o) => {
      const mesh = o as Mesh;
      if (mesh.isMesh) this.surfaces.push(mesh);
    });
    const total = DECAL_SLOTS * DECAL_SLOT_VERTICES;
    this.positions = new Float32Array(total * 3);
    this.uvs = new Float32Array(total * 2);
    this.slotOf = new Float32Array(total);
    for (let slot = 0; slot < DECAL_SLOTS; slot++) {
      this.slotOf.fill(slot, slot * DECAL_SLOT_VERTICES, (slot + 1) * DECAL_SLOT_VERTICES);
    }
    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('uv', new BufferAttribute(this.uvs, 2));
    this.geometry.setAttribute('slot', new BufferAttribute(this.slotOf, 1));
    this.fade = uniformArray(
      Array.from({ length: DECAL_SLOTS }, () => 0),
      'float',
    ) as unknown as UniformArray;
    this.material = this.buildMaterial();
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
    scene.add(this.mesh);
  }

  private buildMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    const slot = attribute<'float'>('slot', 'float');
    const radial = smoothstep(0.18, 0.5, uv().sub(0.5).length()).oneMinus();
    material.colorNode = vec3(0.03, 0.025, 0.02);
    material.opacityNode = radial.mul(this.fade.element(slot.toInt())).mul(0.85);
    material.transparent = true;
    material.blending = NormalBlending;
    material.depthWrite = false;
    material.polygonOffset = true;
    material.polygonOffsetFactor = -2;
    material.polygonOffsetUnits = -2;
    material.mrtNode = effectMrt();
    return material;
  }

  /** Projects a mark of `size` metres onto the arena around `at`, facing `normal`. */
  add(
    at: { x: number; y: number; z: number },
    normal: { x: number; y: number; z: number } | null,
    size: number,
    now: number,
  ): boolean {
    if (this.surfaces.length === 0) return false;
    _normal.set(normal?.x ?? 0, normal?.y ?? 1, normal?.z ?? 0).normalize();
    _q.setFromUnitVectors(_up, _normal);
    _euler.setFromQuaternion(_q);
    _position.set(at.x, at.y, at.z);
    _size.set(size, size, size * 0.6);
    const slot = this.cursor;
    this.cursor = (this.cursor + 1) % DECAL_SLOTS;
    const base = slot * DECAL_SLOT_VERTICES;
    let written = 0;
    for (const surface of this.surfaces) {
      if (written >= DECAL_SLOT_VERTICES) break;
      const decal = new DecalGeometry(surface, _position, _euler, _size);
      const pos = decal.getAttribute('position');
      const uvs = decal.getAttribute('uv');
      const count = Math.min(pos.count, DECAL_SLOT_VERTICES - written);
      // Whole triangles only.
      const triangles = Math.floor(count / 3) * 3;
      for (let i = 0; i < triangles; i++) {
        const v = base + written + i;
        this.positions[v * 3] = pos.getX(i);
        this.positions[v * 3 + 1] = pos.getY(i);
        this.positions[v * 3 + 2] = pos.getZ(i);
        this.uvs[v * 2] = uvs.getX(i);
        this.uvs[v * 2 + 1] = uvs.getY(i);
      }
      written += triangles;
      decal.dispose();
    }
    // Any unused vertices in the slot collapse to a degenerate triangle at the origin.
    for (let i = written; i < DECAL_SLOT_VERTICES; i++) {
      const v = base + i;
      this.positions[v * 3] = 0;
      this.positions[v * 3 + 1] = -1000;
      this.positions[v * 3 + 2] = 0;
    }
    this.born[slot] = written > 0 ? now : -1;
    this.geometry.getAttribute('position').needsUpdate = true;
    this.geometry.getAttribute('uv').needsUpdate = true;
    return written > 0;
  }

  update(now: number): void {
    for (let slot = 0; slot < DECAL_SLOTS; slot++) {
      const born = this.born[slot] ?? -1;
      this.fade.array[slot] = born < 0 ? 0 : Math.max(0, 1 - (now - born) / FADE_SECONDS);
    }
  }

  /** Marks still visible. */
  count(now: number): number {
    return this.born.filter((born) => born >= 0 && now - born < FADE_SECONDS).length;
  }

  clear(): void {
    this.born.fill(-1);
    this.positions.fill(0);
    this.geometry.getAttribute('position').needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
